import { useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { useAsyncStatus, useOnMount } from '../../../hooks';
import { get } from '../../../redux/api';
import { PolicyType } from '../../../types';
import { selectOptionFactory } from '../../../utils';
import type { Policy } from '../../../../typings/api';
import type { FailedFetchResponse } from '../../../helpers/errors';
import type { SelectOption } from '../../../types';

type QualityPolicySelector = {
  policies: Array<SelectOption<number>>;
  selectedPolicies: Array<SelectOption<number>>;
  setSelectedPolicies: Dispatch<SetStateAction<Array<SelectOption<number>>>>;
  isLoading: boolean;
  errors?: string[] | null;
};

/**
 * Hook for fetching quality policies.
 */
const useQualityPolicySelector = (): QualityPolicySelector => {
  const [policies, setPolicies] = useState<Array<SelectOption<number>>>([]);
  const [selectedPolicies, setSelectedPolicies] = useState<Array<SelectOption<number>>>([]);
  const { status, onPending, onSuccess, onError } = useAsyncStatus();

  useOnMount(async () => {
    try {
      onPending();
      const { data } = await get<Policy[]>('/api/policies');

      if (!data?.length) {
        onError('No policies found');
        return;
      }

      const policyOptions = data
        .filter(policy => policy.type === PolicyType.QUALITY)
        .map(policy => selectOptionFactory(policy.id, policy.title));

      setPolicies(policyOptions);
      onSuccess();
    } catch (error) {
      onError(error as FailedFetchResponse);
    }
  });

  return {
    policies,
    selectedPolicies,
    setSelectedPolicies,
    isLoading: status.isLoading,
    errors: status.errors,
  };
};

export default useQualityPolicySelector;
